import { z } from 'zod';

import {
  zodDuration,
  zodPlate,
  zodPlateState,
  zodStringRequired,
  zodTimeStamp,
} from '@/utils/zodUtils';

import { LocationSchema } from './location';
import { Communication, Reservation } from './reservations';
import { createPaginatedResultSchema } from './shared/paginated';
import { ValidationSchema } from './validation';
import { Violation } from './violations';

export const StatusEnum = z.enum([
  'reserved',
  'validated',
  'chalked',
  'violated',
  'in_progress',
  'nearly_expired',
  'expired',
  'passive',
]);
export type Status = z.infer<typeof StatusEnum>;

export const SessionSourceEnum = z.enum(['user', 'passive', 'chalk']);
export type SessionSource = z.infer<typeof SessionSourceEnum>;

export const SessionPlateSchema = z.object({
  number: z.string(),
  state: z.string().optional(),
});
export type SessionPlate = z.infer<typeof SessionPlateSchema>;

export const VehicleSchema = z.object({
  plate: z.string(),
  plateState: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  color: z.string().optional(),
});
export type Vehicle = z.infer<typeof VehicleSchema>;

export const FinancialBreakdownSchema = z.object({
  total_reservation_due: z.number(),
  transaction_fees: z.number(),
  sub_total: z.number(),
  taxes_total: z.number(),
  total_due: z.number(),
  total_paid: z.number().default(0),
  validation_credit: z.number().default(0),
  adjusted_total_due: z.number().default(0),
});
export type FinancialBreakdown = z.infer<typeof FinancialBreakdownSchema>;

export const StatusHistoryEntrySchema = z.object({
  from: StatusEnum,
  to: StatusEnum,
  at: zodTimeStamp,
});
export type StatusHistoryEntry = z.infer<typeof StatusHistoryEntrySchema>;

const ChalkSchema = z.object({
  reason: z.object({
    label: z.string(),
    duration: zodDuration,
  }),
  createdAt: zodTimeStamp,
});
export type Chalk = z.infer<typeof ChalkSchema>;

export const ParkingSessionSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  block_face_id: z.string().uuid().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  lifecycle_id: z.string().optional(),
  status: StatusEnum,
  vehicle: VehicleSchema,
  start_time: zodTimeStamp,
  end_time: zodTimeStamp.nullable(),
  created_at: zodTimeStamp.nullish(),
  updated_at: zodTimeStamp.nullish(),
  status_history: z.array(StatusHistoryEntrySchema).nullish(),
  reservations: z.array(Reservation).nullish(),
  validations: z.array(ValidationSchema).nullish(),
  violations: z.array(Violation).nullish(),
  chalks: z.array(ChalkSchema).nullish(),
  communications: z.array(Communication).nullish(),
  // True Start fields
  entry_time: zodTimeStamp.nullable().optional(),
  entry_source: z.enum(['lpr', 'lpr_fallback', 'fallback']).optional(),
  // Passive Free Parking fields
  source: SessionSourceEnum.nullable().optional(),
  free_minutes_at_creation: z.number().nullable().optional(),
  lpr_entry_time: zodTimeStamp.nullable().optional(),
  lpr_entry_event_id: z.string().nullable().optional(),
  lpr_exit_time: zodTimeStamp.nullable().optional(),
  lpr_exit_event_id: z.string().nullable().optional(),
  plates: z.array(SessionPlateSchema).nullish(),
  has_dispute: z.boolean().optional(),
});

export type ParkingSession = z.infer<typeof ParkingSessionSchema>;

// Schema suitable for list views (e.g., DataGrid)
// Includes validations and reservations for triage view display
export const ParkingSessionListItemSchema = ParkingSessionSchema.pick({
  id: true,
  location_id: true,
  phone: true,
  status: true,
  start_time: true,
  end_time: true,
  vehicle: true,
  validations: true,
  reservations: true,
  source: true,
  has_dispute: true,
});
export type ParkingSessionListItem = z.infer<
  typeof ParkingSessionListItemSchema
>;

const paginationNextKeySchema = z.object({
  id: z.string().uuid(),
});
export type PaginationNextKey = z.infer<typeof paginationNextKeySchema>;

export const ParkingSessionsPaginatedResult = createPaginatedResultSchema(
  ParkingSessionListItemSchema,
  paginationNextKeySchema
);
export type ParkingSessionsPaginatedResultType = z.infer<
  typeof ParkingSessionsPaginatedResult
>;

// Type assertion needed due to TypeScript serialization limits (TS7056) when merging
// large schemas like LocationSchema. The manually defined PrivateSessionResponse type
// below provides the correct compile-time contract. Runtime validation still works correctly.
export const PrivateSessionResponseSchema = ParkingSessionSchema.merge(
  z.object({
    location: LocationSchema,
    financial_breakdown: FinancialBreakdownSchema,
  })
) as unknown as z.ZodType<PrivateSessionResponse>;
export type PrivateSessionResponse = ParkingSession & {
  location: z.infer<typeof LocationSchema>;
  financial_breakdown: FinancialBreakdown;
};

// For detail views, we always get the full PrivateSessionResponse
// which includes both location and financial_breakdown

export const CORRECT_PLATE_REASON_MAX_LENGTH = 500;

// POST /sessions/{session_id}/correct-plate — request body.
// old_plate_* target a specific plate on multi-plate sessions; omit both to
// correct the session's primary plate. plate_state uses the async zodPlateState
// refinement, so this schema must be validated via zodResolver/parseAsync
// (never sync .parse()).
export const CorrectPlateRequestSchema = z.object({
  plate_number: zodPlate,
  plate_state: zodPlateState,
  old_plate_number: zodPlate.optional(),
  old_plate_state: zodPlateState.optional(),
  reason: zodStringRequired('Reason is required').max(
    CORRECT_PLATE_REASON_MAX_LENGTH,
    `Reason cannot exceed ${CORRECT_PLATE_REASON_MAX_LENGTH} characters`
  ),
});
export type CorrectPlateRequest = z.infer<typeof CorrectPlateRequestSchema>;
export type CorrectPlateFormValues = z.input<typeof CorrectPlateRequestSchema>;

// outcome is the state at response time and is always `correct_only`; the audit
// record upgrades to `voided` asynchronously if citations were voided.
export const CorrectPlateOutcomeEnum = z.enum(['correct_only', 'voided']);
export type CorrectPlateOutcome = z.infer<typeof CorrectPlateOutcomeEnum>;

export const CorrectPlateResponseSchema = z.object({
  session_id: z.string().uuid(),
  plate: z.string(),
  plate_state: z.string(),
  outcome: CorrectPlateOutcomeEnum,
});
export type CorrectPlateResponse = z.infer<typeof CorrectPlateResponseSchema>;

// POST /sessions/{session_id}/clear-phone — optional request body.
export const ClearSessionPhoneRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type ClearSessionPhoneRequest = z.infer<
  typeof ClearSessionPhoneRequestSchema
>;

export const ClearSessionPhoneResponseSchema = z.object({
  session_id: z.string().uuid(),
  cleared_session_phone: z.boolean(),
  cleared_reservations: z.number().int().nonnegative(),
  cleared_validations: z.number().int().nonnegative(),
});
export type ClearSessionPhoneResponse = z.infer<
  typeof ClearSessionPhoneResponseSchema
>;
